import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Layers, Phone, ClipboardList, User, Briefcase } from 'lucide-react';
import BackBar from '../components/BackBar';
import Footer from '../components/Footer';
import DocSlot from '../components/DocSlot';
import SplitModal from '../components/SplitModal';
import RegisterGate from '../components/RegisterGate';
import PhoneInput from '../components/PhoneInput';
import useExitIntentGuard from '../components/ExitIntentGuard';
import successWorker from '../assets/success-worker.webp';
import { fetchJob } from '../api/jobs';
import {
  getMe, updateMe, submitApplication, fetchMyApplications, getDashboardDocuments, getApplyDocuments,
  getPortalToken, clearPortalToken, portalErrorMessage, fetchProfessions,
} from '../api/portal';
import { fetchProfiles, updateProfile } from '../api/profiles';
import './ApplyPage.css';

// Candidate apply flow (candidate-apply-rework, 2026-07-26). One screen at a time so a non-tech-savvy
// applicant on a phone is never scrolling a long form.
//   NEW candidate:      auth (email → OTP) → profile (name + phone + preferred comms) → documents → questionnaire → done
//   RETURNING candidate: auth (email → OTP) → documents THEY'RE MISSING → questionnaire → done   (profile skipped — already on file)
// Documents are DATA-DRIVEN from the demand (job.documents); there is no hardcoded CV/passport
// baseline and no profession step (the demand implies the profession, set server-side). Password is
// gone entirely — OTP is the only login path. 'questionnaire' renders only when the demand has
// questions; 'upload' only when there are documents to collect.
// 'professions' (Feature A, 2026-07-28) sits after 'upload' and is shown ONLY for a general (no-demand)
// apply by a NEW applicant — a demand-attached apply derives the profession from the demand, and a
// returning applicant reuses their on-file profile's professions (both server-side).
const STEPS = ['auth', 'profile', 'upload', 'professions', 'questionnaire', 'done'];
// Preferred contact modes offered at registration (multi-select). "Other" reveals a free-text field.
const CONTACT_MODES = ['WhatsApp', 'Call', 'SMS', 'Email', 'Other'];
// Labeled progress reads better than a bare % bar. Hidden on the very first ('auth') step — nothing to
// show progress against yet, and it's one less thing on the highest-drop-off screen.
const STEP_LABELS = { auth: 'Sign in', profile: 'Your details', upload: 'Documents', professions: 'Profession', questionnaire: 'Questions', done: 'Done' };

// Default document slots for the general "send us your CV" flow (no demand attached). A demand-attached
// apply ignores these entirely and drives its slots from job.documents. maxCount must stay in sync with
// backend/src/routes/public/portal.ts's SLOT_MAX_COUNT (separate app, no shared module).
const SLOTS = [
  { type: 'cv', label: 'CV', required: true, singlePage: false, maxCount: 3, hint: 'PDF, Word document, or a clear photo.' },
  { type: 'passport', label: 'Passport', required: true, singlePage: true, maxCount: 1, hint: 'A photo or scan of the passport page with your photo.' },
  { type: 'education', label: 'Education Certificate', required: false, singlePage: false, maxCount: 5 },
  { type: 'experience', label: 'Experience Certificate', required: false, singlePage: false, maxCount: 5 },
  { type: 'other', label: 'Other Documents', required: false, singlePage: false, maxCount: 5 },
];
const SLOT_MAX_BY_TYPE = Object.fromEntries(SLOTS.map((s) => [s.type, s.maxCount]));
const SLOT_META_BY_TYPE = Object.fromEntries(SLOTS.map((s) => [s.type, { singlePage: s.singlePage, maxCount: s.maxCount, hint: s.hint }]));

// DocumentType name → built-in slot key. Mirror of the backend's DOCTYPE_NAME_TO_SLOT
// (publicJobProjection.ts) — lets a submit 400 missing-doc, given only by name, reuse the CV / passport
// special slot handling instead of a plain `dt:` upload when it maps to a built-in.
const DOCTYPE_NAME_TO_SLOT = {
  'CV': 'cv',
  'Travel Document (Passport)': 'passport',
  'Passport Copy': 'passport', // legacy synonym (being retired) — mirror publicJobProjection.ts
  'Education Certificate': 'education',
  'Experience Certificate': 'experience',
  'Police Clearance': 'police_clearance',
  'Other Documents': 'other',
};

// Build a DocSlot descriptor for any slot key. Built-ins inherit their special UX (cv = multi-file,
// passport = single-page picker, etc.); everything else (`dt:<id>`, police_clearance) is a plain
// single-file upload. The requirement (mandatory/optional) always comes from the caller/data now.
function makeSlot(type, label, required) {
  const meta = SLOT_META_BY_TYPE[type] || {};
  return {
    type,
    label,
    required: !!required,
    singlePage: type === 'passport' ? true : !!meta.singlePage,
    maxCount: meta.maxCount ?? 1,
    hint: meta.hint,
  };
}

// Demand-configured documents ([{ slot, name, requirement }], mandatory-first from the server) →
// DocSlot descriptors, purely data-driven. No hardcoded CV/passport baseline — job.documents is the
// single source of what to collect, and the requirement comes straight from the data.
function buildDemandSlots(jobDocs) {
  return (Array.isArray(jobDocs) ? jobDocs : []).map((d) => makeSlot(d.slot, d.name || d.slot, d.requirement === 'mandatory'));
}

// Submit-time 400 missing document ({ documentTypeId, name }) → the slot key to collect it under.
function missingDocSlotType(md) {
  return DOCTYPE_NAME_TO_SLOT[md.name] || `dt:${md.documentTypeId}`;
}

// Names are the canonical cross-reference between a demand's configured doc, its DocumentType, and a
// candidate's held doc (built-in slot name === DocumentType name === candidate doc name). Normalise
// before comparing so trivial case/spacing differences don't cause a held doc to be re-requested.
const normName = (s) => String(s || '').trim().toLowerCase();

// First-space split — the backend stores firstName/lastName as two columns even though the UI shows a
// single "Full name" field (same convention as RegisterGate / LoginPage / DashboardPage).
function splitName(full) {
  const trimmed = String(full || '').trim().replace(/\s+/g, ' ');
  const spaceAt = trimmed.indexOf(' ');
  return spaceAt === -1 ? { firstName: trimmed, lastName: '' } : { firstName: trimmed.slice(0, spaceAt), lastName: trimmed.slice(spaceAt + 1) };
}

// Steps that are TERMINAL (or pre-entry) and must therefore show no wizard chrome and arm no
// exit-intent warning. Hoisted 2026-08-25: this list existed as two separate literals and adding the
// 'notice' step missed one of them, which is the third time this second-consumer trap has bitten this
// file. Keep it as ONE list. 'notice' is not in flowSteps, so a stale copy also yields a 0% progress bar.
const NO_CHROME_STEPS = ['auth', 'done', 'already-applied', 'notice', 'confirm'];

// Q4 = C (owner decision, 2026-09-26): the portal refuses a NEW registration that matches someone
// already on file (same phone + a similar name — see the backend's utils/portalDuplicateGuard.ts).
// One title used at every point this can surface; the MESSAGE itself always comes from the server
// (POST /applications' 409 `error`, or GET /me's `alreadyRegisteredMessage`) — never typed twice.
const ALREADY_REGISTERED_TITLE = "You're already registered";

export default function ApplyPage() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  // General "send your CV" flow (no demand): the server-enforced required doc set (built-ins ∪ the
  // admin's mandatoryForAll), fetched on mount. null until loaded / on failure → baseSlotsFor falls
  // back to the hardcoded SLOTS. A demand-attached apply ignores this (it drives slots from job.documents).
  const [generalDocs, setGeneralDocs] = useState(null);
  const [step, setStep] = useState('auth');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Server-discriminated 409s that are NOT "you already applied to this role" (see the 409
  // handler below): { title, message }, rendered by the 'notice' step.
  const [notice, setNotice] = useState(null);
  // True only when a portal token already exists, i.e. the mount effect below is about to run —
  // avoids a render flash of the 'auth' screen for an already-signed-in visitor (e.g. arriving from the
  // Dashboard "Continue registration" gate) before the async post-auth routing resolves.
  const [checkingSession, setCheckingSession] = useState(() => !!getPortalToken());

  // Scroll to the top of each new step — moving between wizard steps never changes the URL, so App.js's
  // sitewide ScrollToTop (which fires on pathname change) doesn't cover it. Instant, same as ScrollToTop.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [step]);

  // Warn on tab close/reload once the candidate has started entering real data — never on the first
  // ('auth') screen, the terminal screens, or 'confirm' (nothing entered there).
  useExitIntentGuard(!NO_CHROME_STEPS.includes(step));

  // Single "Full name" field, split to firstName/lastName on save. Seeded from the account for an
  // already-signed-in / returning candidate (used in the done-screen greeting).
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [contactModes, setContactModes] = useState([]);
  const [contactModeOther, setContactModeOther] = useState('');
  const [openToOtherRoles, setOpenToOtherRoles] = useState(true);

  // Returning = the account already has a name + phone on file (so the profile step is skipped).
  const [returning, setReturning] = useState(false);
  // Names of documents the candidate already holds (returning candidate) — subtracted from the demand's
  // configured docs so they only upload what they're actually missing. Empty for a brand-new candidate.
  const [heldDocNames, setHeldDocNames] = useState(() => new Set());
  // Set only when a submit 400s with missingDocuments (the reused profile is short a required doc, or a
  // race) — overrides the upload step to collect exactly those, then re-submit.
  const [extraMissingSlots, setExtraMissingSlots] = useState(null);

  // docs[type] = [{ stageId, pageCount, thumbnails }, …] — an array per slot (multi-file slots).
  const [docs, setDocs] = useState({});
  const [splitOpen, setSplitOpen] = useState(false);

  // Questionnaire answers, keyed by question id. Checkbox stores a boolean; number/text/date store the
  // raw input string. Mapped to the backend's [{ questionId, value }] shape at submit.
  const [answers, setAnswers] = useState({});

  // Feature A (2026-07-28): profession selection for a general (no-demand) apply. professionList is
  // fetched on mount; primaryProfession (required) + up to 3 otherProfessions (ids from the ERP table);
  // otherProfessionText is optional free-text for professions not in the list (stored raw for staff to
  // map — public users never create Profession rows).
  const [professionList, setProfessionList] = useState([]);
  const [professionsLoaded, setProfessionsLoaded] = useState(false); // false until the fetch settles (ok or fail)
  const [primaryProfession, setPrimaryProfession] = useState('');
  const [otherProfessions, setOtherProfessions] = useState([]);
  const [otherProfessionText, setOtherProfessionText] = useState('');
  // A returning applicant's on-file CandidateProfile id — set after auth (below) for a general apply, so
  // the profession step can pre-fill their current professions and write any change back to the profile
  // before submit (the apply reuses the profile server-side, ignoring request-body professions).
  const [profileId, setProfileId] = useState(null);

  useEffect(() => {
    if (!slug) return;
    fetchJob(slug).then(setJob).catch(() => setJob(null));
  }, [slug]);

  // General apply (no slug): fetch the required doc set the server actually enforces so every required
  // document is collected in ONE screen. Without this the hardcoded SLOTS omit the admin's
  // mandatoryForAll docs, the submit 400s, and the candidate hits a second "add this document" screen.
  useEffect(() => {
    if (slug) return;
    getApplyDocuments().then(setGeneralDocs).catch(() => setGeneralDocs(null));
  }, [slug]);

  // Feature A: load the ERP profession list for the general-apply profession step (no demand attached).
  // Always resolves professionsLoaded (even on failure / a kill-switched endpoint) so the step can
  // degrade to a free-text-only path instead of dead-ending on an empty, un-submittable picker.
  useEffect(() => {
    if (slug) return;
    fetchProfessions()
      .then((rows) => setProfessionList(Array.isArray(rows) ? rows : []))
      .catch(() => setProfessionList([]))
      .finally(() => setProfessionsLoaded(true));
  }, [slug]);

  // Per-demand questionnaire (from the job projection). Empty for a general apply or a demand with no
  // questions — in which case the whole 'questionnaire' step is skipped.
  const questionnaire = Array.isArray(job?.questionnaire) ? job.questionnaire : [];
  const hasQuestionnaire = questionnaire.length > 0;

  // The base document set for the current context: a demand's own configured docs (data-driven), or the
  // general-apply defaults when there's no demand. A demand with NO configured docs requires nothing.
  function baseSlotsFor(j, gdocs = generalDocs) {
    if (slug) return Array.isArray(j?.documents) && j.documents.length ? buildDemandSlots(j.documents) : [];
    // General apply: the server-enforced required set (built-ins ∪ mandatoryForAll), built the same way
    // as a demand's docs. Fall back to the hardcoded SLOTS only until the mount fetch resolves / if it fails.
    return Array.isArray(gdocs) ? buildDemandSlots(gdocs) : SLOTS;
  }
  // Slots to actually show/collect: the error-driven missing set if present, else the base set minus
  // whatever the candidate already holds (a no-op for a brand-new candidate).
  const activeUploadSlots = extraMissingSlots
    ? extraMissingSlots
    : baseSlotsFor(job).filter((s) => !heldDocNames.has(normName(s.label)));

  const canSubmit = activeUploadSlots.every((s) => {
    if (!s.required) return true;
    const items = docs[s.type] || [];
    if (items.length === 0) return false;
    if (s.type === 'passport') return items[0]?.pageCount === 1;
    return true;
  });

  const questionnaireComplete = questionnaire.every((q) => {
    if (!q.required) return true;
    const v = answers[q.id];
    return q.type === 'checkbox' ? v === true : v != null && String(v).trim() !== '';
  });

  // After auth (fresh OTP verify, or an already-signed-in token found on mount):
  // 1. Demand-attached apply → check FIRST whether this account already applied to this exact job and
  //    surface 'already-applied' immediately, before any work.
  // 2. Otherwise route on whether the account is brand-new (no name/phone → 'profile' step) or returning
  //    (has them → skip straight to uploading only the docs it's missing / the questionnaire / a confirm).
  // Never blocks the applicant on a transient failure: any error falls back to the fresh-profile path.
  async function goToPostAuthStep(meMaybe) {
    try {
      const me = meMaybe || await getMe();

      // Q4 C: a brand-new registration already flagged as already-registered (e.g. resuming a
      // session whose name+phone were saved earlier) is stopped here — before any profile/upload
      // work — same as the 409 the submit itself would hit.
      if (me.alreadyRegistered) {
        setNotice({ title: ALREADY_REGISTERED_TITLE, message: me.alreadyRegisteredMessage, code: 'ALREADY_REGISTERED' });
        setStep('notice');
        return;
      }

      setFullName(me.name || [me.firstName, me.lastName].filter(Boolean).join(' '));
      setPhone(me.phone || '');

      const apps = slug ? await fetchMyApplications().catch(() => []) : [];
      if (slug && apps.some((a) => a.job?.slug === slug)) { setStep('already-applied'); return; }

      // Make sure the job (and therefore its documents + questionnaire) is loaded before choosing the
      // first step — the mount-triggered call can race ahead of the initial job fetch.
      let j = job;
      if (slug && !j) { j = await fetchJob(slug).catch(() => null); if (j) setJob(j); }
      // Same for the general required-doc set (no-slug flow) — resolve it before diffing against held
      // docs so a returning candidate's "which docs are you missing" routing doesn't race the mount fetch.
      let gd = generalDocs;
      if (!slug && !Array.isArray(gd)) { gd = await getApplyDocuments().catch(() => null); if (Array.isArray(gd)) setGeneralDocs(gd); }
      const hasQ = Array.isArray(j?.questionnaire) && j.questionnaire.length > 0;

      const isReturning = !!(me.phone && (me.name || me.firstName));
      setReturning(isReturning);

      if (isReturning) {
        // Existing candidate: reuse their on-file profile server-side. Show only the docs they DON'T
        // already hold (the demand's set minus what GET /dashboard/documents returns, matched by name).
        let held = new Set();
        try {
          const heldDocs = await getDashboardDocuments();
          // Exclude REJECTED docs from "held" so a required doc whose only copy was rejected is
          // re-collected during apply rather than silently suppressed (code-review MEDIUM 2026-07-26).
          held = new Set((heldDocs || []).filter((d) => d.status !== 'rejected').map((d) => normName(d.name)));
        } catch { /* no candidate row yet — treat as holding nothing */ }
        setHeldDocNames(held);
        // Feature A follow-up (2026-07-29 owner ask): a returning applicant ALSO goes through the
        // general-apply profession step now — pre-fill it from their on-file profile so they can review
        // and adjust what work they're looking for (the step writes any change back to the profile
        // before submit, since the apply reuses the profile server-side).
        if (!slug) {
          try {
            const prof = (await fetchProfiles())?.[0] || null; // single-profile-per-account model
            if (prof) {
              setProfileId(prof.id);
              if (prof.professionId) setPrimaryProfession(prof.professionId);
              if (Array.isArray(prof.otherProfessionIds)) setOtherProfessions(prof.otherProfessionIds);
            }
          } catch { /* non-fatal — the step just starts blank */ }
        }
        const missing = baseSlotsFor(j, gd).filter((s) => !held.has(normName(s.label)));
        if (missing.length > 0) setStep('upload');
        else if (hasQ) setStep('questionnaire');
        else if (!slug) setStep('professions'); // general apply: pick/confirm profession(s) before submit
        else setStep('confirm');
      } else {
        setStep('profile');
      }
    } catch {
      setStep('profile');
    }
  }

  useEffect(() => {
    if (!getPortalToken()) return;
    (async () => {
      try {
        const me = await getMe();
        await goToPostAuthStep(me);
      } catch {
        clearPortalToken();
      } finally {
        setCheckingSession(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional mount-only run

  // RegisterGate is email-only now; the account row is upserted server-side on verify. Route on the
  // freshly-fetched account (new → profile step; returning → skip to the docs it's missing).
  async function handleVerified() {
    await goToPostAuthStep();
  }

  function setAnswer(qid, value) {
    setError('');
    setAnswers((prev) => ({ ...prev, [qid]: value }));
  }

  function toggleContactMode(m) {
    setContactModes((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  }

  // Add one more staged file to a slot (a fresh upload, or a passport page pick — DocSlot itself hides
  // its "Add" trigger once a slot reaches maxCount, this is just a backstop).
  function onSlotAdd(type, value) {
    setError('');
    setDocs((prev) => {
      const existing = prev[type] || [];
      if (existing.length >= (SLOT_MAX_BY_TYPE[type] ?? 1)) return prev;
      return { ...prev, [type]: [...existing, value] };
    });
  }
  function onSlotRemoveAt(type, index) {
    setError('');
    setDocs((prev) => ({ ...prev, [type]: (prev[type] || []).filter((_, i) => i !== index) }));
  }

  // Assembled docs from a combined-file split populate the matching slots (append, same maxCount
  // backstop as onSlotAdd — the split modal returns at most one combined doc per type).
  function onAssembled(assembled) {
    setDocs((prev) => {
      const next = { ...prev };
      for (const d of assembled) {
        const existing = next[d.type] || [];
        if (existing.length >= (SLOT_MAX_BY_TYPE[d.type] ?? 1)) continue;
        next[d.type] = [...existing, { stageId: d.stageId, pageCount: d.pageCount, thumbnails: [] }];
      }
      return next;
    });
    setSplitOpen(false);
  }

  // [{ questionId, value }] for the backend: checkbox → 'true'/'false', everything else → the string.
  function buildAnswersPayload() {
    return questionnaire.map((q) => ({
      questionId: q.id,
      value: q.type === 'checkbox' ? (answers[q.id] ? 'true' : 'false') : String(answers[q.id] ?? ''),
    }));
  }

  const profileComplete = fullName.trim() && phone.trim() && contactModes.length > 0
    && (!contactModes.includes('Other') || contactModeOther.trim()); // require detail when "Other" is chosen

  // Profile step (new candidates): save name + phone to the account, then advance to documents /
  // questionnaire / submit. The chosen contact modes ride along in the final submit, not /me.
  async function handleProfileNext(e) {
    e?.preventDefault();
    if (!profileComplete || busy) return;
    setBusy(true); setError('');
    let ok = false;
    try {
      const { firstName, lastName } = splitName(fullName);
      await updateMe({ firstName, lastName, phone: phone.trim() });
      ok = true;
    } catch (err) {
      setError(portalErrorMessage(err, 'Could not save your details. Please try again.'));
    } finally { setBusy(false); }
    if (!ok) return;

    // Q4 C: name+phone are only just saved for a brand-new registration, so this is the first point
    // they can be compared — re-check before the upload step. Best-effort: a failed check here must
    // never strand a genuine applicant, so any error just falls through to the normal routing below.
    try {
      const me = await getMe();
      if (me.alreadyRegistered) {
        setNotice({ title: ALREADY_REGISTERED_TITLE, message: me.alreadyRegisteredMessage, code: 'ALREADY_REGISTERED' });
        setStep('notice');
        return;
      }
    } catch { /* non-fatal — fall through to the normal next-step routing */ }

    if (activeUploadSlots.length > 0) setStep('upload');
    else if (!slug) setStep('professions'); // Feature A: general apply picks profession(s) (new + returning)
    else if (hasQuestionnaire) setStep('questionnaire');
    else handleSubmit();
  }

  // Upload step → profession step (general apply) / questionnaire (demand) / submit.
  function handleUploadNext(e) {
    e?.preventDefault();
    if (!canSubmit || busy) return;
    if (!slug) setStep('professions'); // Feature A (general apply — new + returning)
    else if (hasQuestionnaire) setStep('questionnaire');
    else handleSubmit();
  }

  // Profession step (general apply) → submit. Require a primary only when the list actually loaded with
  // options; if the fetch failed / the endpoint is off, the applicant proceeds with just the optional
  // free-text (a general apply accepts no profession server-side). Wait for the fetch to settle first.
  async function handleProfessionsNext(e) {
    e?.preventDefault();
    if (busy || !professionsLoaded || (professionList.length > 0 && !primaryProfession)) return;
    // A returning applicant's apply reuses their on-file profile SERVER-SIDE (by accountId) and ignores
    // request-body professions, so persist the chosen selection onto that profile FIRST — then the reused
    // profile carries the latest choice. A NEW applicant has no profile yet (profileId null): the submit's
    // body professions are used and a profile is auto-created from them, so nothing to write here.
    // Non-fatal on failure — still submit (the free-text + declared-profession activity capture intent).
    if (profileId && primaryProfession) {
      setBusy(true);
      try { await updateProfile(profileId, { professionId: primaryProfession, otherProfessionIds: otherProfessions }); }
      catch { /* keep going */ }
      setBusy(false);
    }
    handleSubmit();
  }

  // Questionnaire step → submit.
  function handleQuestionnaireNext(e) {
    e?.preventDefault();
    if (!questionnaireComplete || busy) return;
    handleSubmit();
  }

  async function handleSubmit() {
    if (busy) return;
    setBusy(true); setError('');
    try {
      // Build from docs entries (not a fixed slot order) so arbitrary `dt:<id>` demand-doc slots are
      // included, not just the built-in types. Empty for the reuse-my-details confirm path.
      const list = Object.entries(docs).flatMap(([t, items]) => (items || []).map((d) => ({ stageId: d.stageId, type: t })));
      await submitApplication({
        demandSlug: slug,
        docs: list,
        answers: buildAnswersPayload(),
        contactModes,
        contactModeOther: contactModes.includes('Other') ? contactModeOther.trim() : '',
        openToOtherRoles,
        // Feature A: profession selection — only for a general apply. Ignored server-side for a
        // demand-attached apply (profession derived from the demand) or a profile-reuse apply.
        professionId: (!slug && primaryProfession) ? primaryProfession : undefined,
        otherProfessionIds: (!slug && otherProfessions.length) ? otherProfessions : undefined,
        otherProfessionText: (!slug && otherProfessionText.trim()) ? otherProfessionText.trim() : undefined,
      });
      setExtraMissingSlots(null);
      setStep('done');
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      // Already applied while we weren't looking. NOT every 409 means that, though: the general
      // apply (/apply, no slug) has no role, so the 'already-applied' screen — which says "an
      // application in for this role" under a green tick — is wrong copy there. The server tags the
      // two non-role cases with a `code`; demand-attached 409s deliberately carry none.
      if (status === 409) {
        if (data?.code === 'GENERAL_APPLY_COOLDOWN') {
          setNotice({ title: 'We already have your CV', message: data.error });
          setStep('notice'); return;
        }
        if (data?.code === 'APPLICATION_IN_FLIGHT') {
          setNotice({ title: 'Just a moment', message: data.error });
          setStep('notice'); return;
        }
        if (data?.code === 'ALREADY_REGISTERED') {
          setNotice({ title: ALREADY_REGISTERED_TITLE, message: data.error, code: 'ALREADY_REGISTERED' });
          setStep('notice'); return;
        }
        setStep('already-applied'); return;
      }
      // The demand still needs specific documents (the reused profile is short one, or a race) — drop
      // into the upload step showing exactly those, so the candidate can add them and re-submit.
      if (status === 400 && Array.isArray(data?.missingDocuments) && data.missingDocuments.length) {
        const slots = data.missingDocuments.map((md) => makeSlot(missingDocSlotType(md), md.name, true));
        setExtraMissingSlots(slots);
        const names = data.missingDocuments.map((d) => d.name).filter(Boolean).join(', ');
        setError(names ? `Please add ${names} to continue.` : (data.error || 'Some required documents are still needed.'));
        setStep('upload');
        return;
      }
      // Required questions unanswered — send them back to the questionnaire.
      if (status === 400 && Array.isArray(data?.missingQuestions) && data.missingQuestions.length) {
        setError(data.error || 'Please answer all required questions.');
        setStep('questionnaire');
        return;
      }
      // The job closed or was unpublished between page load and submit. The server now gates the
      // demand lookup with PUBLIC_WHERE and 404s instead of silently downgrading to a general apply,
      // and fetchJob's own 404 is swallowed above (job stays null) — so without this branch the
      // wizard parks on a red "Job not found" and re-submits the same dead slug forever, with no way
      // out. Terminal notice + the links out of it is the exit.
      // `&& slug` matters: this endpoint also 404s for the admin `public.apply` kill-switch and for a
      // deleted account, neither of which is a closed job — and a general apply has no job to close.
      // Gate on slug rather than a server `code`, which would be inert during the portal-first deploy
      // window when the portal is new but the backend is not.
      if (status === 404 && slug) {
        setNotice({
          title: 'This job has closed',
          message: "That role isn't taking applications any more. Nothing you entered is lost — browse the current openings, or send us your CV and we'll match you to new roles as they open.",
        });
        setStep('notice');
        return;
      }
      // phone-required (400) and everything else — surface the message as-is.
      setError(portalErrorMessage(err, 'Could not submit your application. Please try again.'));
    } finally { setBusy(false); }
  }

  // The linear flow shown in the labelled progress bar — the canonical STEPS order minus the steps that
  // don't apply to this candidate/demand (profile for a returning account, upload when nothing to
  // collect, questionnaire when the demand has none).
  const flowSteps = STEPS.filter((s) => {
    if (s === 'profile') return !returning;
    if (s === 'upload') return activeUploadSlots.length > 0;
    if (s === 'professions') return !slug; // Feature A: shown for every general apply (new + returning)
    if (s === 'questionnaire') return hasQuestionnaire;
    return true; // 'auth', 'done'
  });
  const stepIndex = flowSteps.indexOf(step);
  const progressPct = step === 'done' ? 100 : Math.round((Math.max(stepIndex, 0) / (flowSteps.length - 1)) * 100);

  // Back navigation only ever moves one real step earlier — never back to 'auth' (the OTP was already
  // sent; RegisterGate's own "Edit your details" is the escape hatch there).
  const stepOrder = flowSteps.filter((s) => s !== 'done');
  const backStep = (() => { const i = stepOrder.indexOf(step); return i > 1 ? stepOrder[i - 1] : null; })();
  function goBack() { if (backStep) setStep(backStep); }

  const showChrome = !NO_CHROME_STEPS.includes(step);

  return (
    <>
      <BackBar variant="task" backTo={slug ? `/jobs/${slug}` : '/'} backLabel={slug ? 'Back to job' : 'All jobs'} />
      <main className="apply-main container">
        <div className="apply-card">
          {checkingSession ? (
            <div className="apply-loading"><Loader2 className="spin" size={22} /></div>
          ) : (
          <>
          {showChrome && (
            <div className="apply-head">
              <h1 className="apply-title">Apply{job?.title ? ` — ${job.title}` : ''}</h1>
              <p className="apply-sub">
                {job?.title ? 'A few quick steps and your documents are with our team.'
                  : 'Send us your CV — we will match you to open roles across the Gulf.'}
              </p>
            </div>
          )}

          {showChrome && !extraMissingSlots && (
            <>
              <div className="apply-steps" aria-hidden="true">
                {flowSteps.slice(0, -1).map((s) => (
                  <span key={s} className={s === step ? 'apply-step-on' : ''}>{STEP_LABELS[s]}</span>
                ))}
              </div>
              <div className="apply-progress">
                <div className="apply-progress-bar" style={{ width: `${progressPct}%` }} />
              </div>
            </>
          )}

          {error && <div className="apply-error" role="alert">{error}</div>}

          {/* STEP — auth (email-only → CAPTCHA-protected OTP, via RegisterGate) */}
          {step === 'auth' && <RegisterGate showName={false} showPhone={false} onVerified={handleVerified} />}

          {/* STEP — already applied (demand-attached apply only; checked immediately after auth) */}
          {step === 'already-applied' && (
            <div className="apply-step apply-done">
              <CheckCircle2 size={40} className="apply-done-icon" />
              <h2>You've already applied</h2>
              <p>You already have an application in for this role. Our team will be in touch — no need to apply again.</p>
              <Link to="/dashboard" className="apply-btn apply-btn-link">View my applications</Link>
              <Link to="/" className="apply-link-btn" style={{ marginTop: '0.75rem', display: 'inline-block' }}>Browse other jobs</Link>
            </div>
          )}

          {/* STEP — notice. Server-discriminated 409s that are not "already applied to this role":
              the general-apply cooldown, and the concurrent-submit race. Renders the server's own
              message so that copy lives in exactly one place. */}
          {step === 'notice' && notice && (
            <div className="apply-step apply-done">
              <CheckCircle2 size={40} className="apply-done-icon" />
              <h2>{notice.title}</h2>
              <p>{notice.message}</p>
              {notice.code === 'ALREADY_REGISTERED' ? (
                // "View my applications" would be wrong for this person — they aren't signed into
                // the account that's actually registered.
                <Link to="/login" className="apply-btn apply-btn-link">Sign in with a different email</Link>
              ) : (
                <Link to="/dashboard" className="apply-btn apply-btn-link">View my applications</Link>
              )}
              <Link to="/" className="apply-link-btn" style={{ marginTop: '0.75rem', display: 'inline-block' }}>Browse jobs</Link>
            </div>
          )}

          {/* STEP — confirm (returning account with nothing left to collect: no missing docs, no
              questions). Reuses the on-file profile server-side. */}
          {step === 'confirm' && (
            <div className="apply-step apply-confirm">
              <h2>{job?.title ? `Apply to ${job.title} with your saved details?` : 'Submit your application with your saved details?'}</h2>
              <p className="apply-hint" style={{ marginTop: 0 }}>
                We already have your documents and profile on file — no need to upload anything again.
              </p>
              <button type="button" className="apply-btn" onClick={handleSubmit} disabled={busy}>
                {busy ? <Loader2 className="spin" size={18} /> : null} Submit application
              </button>
            </div>
          )}

          {/* STEP — profile (new candidates only): full name + contact number + preferred comms. */}
          {step === 'profile' && (
            <form className="apply-step" onSubmit={handleProfileNext}>
              <label className="apply-label" htmlFor="apply-name"><User size={18} /> Your details</label>
              <input id="apply-name" type="text" autoComplete="name" autoFocus
                className="apply-input" placeholder="Full name (as on your passport)"
                value={fullName} onChange={(e) => setFullName(e.target.value.toUpperCase())} disabled={busy} />

              <label className="apply-label" htmlFor="apply-phone" style={{ marginTop: '1rem' }}>
                <Phone size={18} /> Contact number
              </label>
              <PhoneInput id="apply-phone" value={phone} onChange={setPhone} disabled={busy} />

              <label className="apply-label" style={{ marginTop: '1.25rem' }}>How should we contact you?</label>
              <p className="apply-hint" style={{ marginTop: 0 }}>Choose all that apply — this is how our team will reach you.</p>
              {CONTACT_MODES.map((m) => (
                <label key={m} className="apply-checkbox-row">
                  <input type="checkbox" checked={contactModes.includes(m)} onChange={() => toggleContactMode(m)} disabled={busy} />
                  <span>{m}</span>
                </label>
              ))}
              {contactModes.includes('Other') && (
                <input type="text" className="apply-input" style={{ marginTop: '0.5rem' }}
                  placeholder="Tell us your preferred way to reach you" maxLength={200}
                  value={contactModeOther} onChange={(e) => setContactModeOther(e.target.value)} disabled={busy} />
              )}

              <label className="apply-checkbox-row" style={{ marginTop: '1rem' }}>
                <input type="checkbox" checked={openToOtherRoles} onChange={(e) => setOpenToOtherRoles(e.target.checked)} disabled={busy} />
                <span>Consider me for other suitable roles too</span>
              </label>

              {!profileComplete && (
                <p className="apply-hint apply-gate">Add your name, contact number, and at least one way for us to reach you.</p>
              )}
              <button type="submit" className="apply-btn" disabled={!profileComplete || busy}>
                {busy ? <Loader2 className="spin" size={18} /> : null} Continue
              </button>
            </form>
          )}

          {/* STEP — typed document uploads (data-driven from the demand; a returning candidate sees only
              the docs they're missing) */}
          {step === 'upload' && (
            <form className="apply-step" onSubmit={handleUploadNext}>
              {backStep && (
                <button type="button" className="apply-link-btn apply-back-link" onClick={goBack}>&larr; Back</button>
              )}
              <p className="apply-hint" style={{ marginTop: 0 }}>
                {extraMissingSlots
                  ? 'This job needs a couple more documents from you. Each is saved as a PDF you can preview.'
                  : returning
                    ? 'Add the documents below to finish applying. Each is saved as a PDF you can preview.'
                    : 'Add your documents. Each is saved as a PDF you can preview.'}
              </p>
              <button type="button" className="apply-split-link" onClick={() => setSplitOpen(true)}>
                <Layers size={16} /> Everything in one file? Split it into documents &rarr;
              </button>

              {activeUploadSlots.map((s) => (
                <DocSlot key={s.type} type={s.type} label={s.label} hint={s.hint}
                  required={s.required} singlePage={s.singlePage} max={s.maxCount}
                  items={docs[s.type] || []} onAdd={onSlotAdd} onRemoveAt={onSlotRemoveAt} disabled={busy} />
              ))}

              {!canSubmit && (
                <p className="apply-hint apply-gate">Add the documents marked “required” to continue.</p>
              )}
              <button type="submit" className="apply-btn" disabled={!canSubmit || busy}>
                {busy ? <Loader2 className="spin" size={18} /> : null} {(hasQuestionnaire || !slug) ? 'Continue' : 'Submit application'}
              </button>
            </form>
          )}

          {/* STEP — profession selection (Feature A; general new apply only, after documents). Pick 1
              primary + up to 3 others from the ERP table, plus optional free-text for anything not listed. */}
          {step === 'professions' && (
            <form className="apply-step" onSubmit={handleProfessionsNext}>
              {backStep && (
                <button type="button" className="apply-link-btn apply-back-link" onClick={goBack}>&larr; Back</button>
              )}
              <label className="apply-label"><Briefcase size={18} /> What work are you looking for?</label>
              <p className="apply-hint" style={{ marginTop: 0 }}>
                {professionList.length > 0
                  ? 'Pick your main profession, and up to 3 more you would also do.'
                  : professionsLoaded
                    ? 'Tell us what work you’re looking for below and our team will match you to the right roles.'
                    : 'Loading professions…'}
              </p>

              {/* Hide the picker if the list couldn't load / is empty — the free-text below still lets the
                  applicant declare a profession, and the step no longer dead-ends (review MEDIUM). */}
              {(professionList.length > 0 || !professionsLoaded) && (
                <>
                  <label className="apply-q-label" htmlFor="primary-profession">Main profession *</label>
                  <select id="primary-profession" className="apply-input" value={primaryProfession} disabled={busy || professionList.length === 0}
                    onChange={(e) => { const v = e.target.value; setPrimaryProfession(v); setOtherProfessions((o) => o.filter((id) => id !== v)); }}>
                    <option value="">Select your main profession…</option>
                    {professionList.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                  </select>
                </>
              )}

              {primaryProfession && (
                <>
                  <label className="apply-q-label" style={{ marginTop: '1rem' }}>
                    Other professions you would also do <span style={{ fontWeight: 400 }}>(optional, up to 3)</span>
                  </label>
                  <select className="apply-input" value="" disabled={busy || otherProfessions.length >= 3}
                    onChange={(e) => { const id = e.target.value; if (id && !otherProfessions.includes(id) && otherProfessions.length < 3) setOtherProfessions((o) => [...o, id]); }}>
                    <option value="">{otherProfessions.length >= 3 ? 'Up to 3 added' : 'Add another profession…'}</option>
                    {professionList.filter((p) => p.id !== primaryProfession && !otherProfessions.includes(p.id)).map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  {otherProfessions.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                      {otherProfessions.map((id) => {
                        const p = professionList.find((x) => x.id === id);
                        return (
                          <span key={id} className="apply-chip apply-chip-added">
                            {p?.name || id}
                            <button type="button" aria-label={`Remove ${p?.name || ''}`} disabled={busy}
                              onClick={() => setOtherProfessions((o) => o.filter((x) => x !== id))}>&times;</button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              <label className="apply-q-label" htmlFor="other-profession-text" style={{ marginTop: '1rem' }}>
                Can’t find your profession? Type it here <span style={{ fontWeight: 400 }}>(optional)</span>
              </label>
              <input id="other-profession-text" className="apply-input" type="text" maxLength={500}
                placeholder="e.g. Shawarma Chef, Rig Welder" disabled={busy}
                value={otherProfessionText} onChange={(e) => setOtherProfessionText(e.target.value)} />
              <p className="apply-hint" style={{ marginTop: '0.25rem' }}>Our team will match this to the right role for you.</p>

              {professionList.length > 0 && !primaryProfession && (
                <p className="apply-hint apply-gate">Choose your main profession to continue.</p>
              )}
              <button type="submit" className="apply-btn" disabled={busy || !professionsLoaded || (professionList.length > 0 && !primaryProfession)}>
                {busy ? <Loader2 className="spin" size={18} /> : null} Submit application
              </button>
            </form>
          )}

          {/* STEP — questionnaire (demand-attached apply only, after documents). One input per question. */}
          {step === 'questionnaire' && (
            <form className="apply-step" onSubmit={handleQuestionnaireNext}>
              {backStep && (
                <button type="button" className="apply-link-btn apply-back-link" onClick={goBack}>&larr; Back</button>
              )}
              <label className="apply-label"><ClipboardList size={18} /> A few questions for this role</label>
              <p className="apply-hint" style={{ marginTop: 0 }}>Please answer the questions below to continue.</p>
              {questionnaire.map((q) => (
                <div className="apply-question" key={q.id}>
                  {q.type === 'checkbox' ? (
                    <label className="apply-checkbox-row">
                      <input type="checkbox" checked={answers[q.id] === true}
                        onChange={(e) => setAnswer(q.id, e.target.checked)} disabled={busy} />
                      <span>{q.text}{q.required ? ' *' : ''}</span>
                    </label>
                  ) : (
                    <>
                      <label className="apply-q-label" htmlFor={`q-${q.id}`}>{q.text}{q.required ? ' *' : ''}</label>
                      <input id={`q-${q.id}`} className="apply-input"
                        type={q.type === 'number' ? 'number' : q.type === 'date' ? 'date' : 'text'}
                        value={answers[q.id] ?? ''} onChange={(e) => setAnswer(q.id, e.target.value)} disabled={busy} />
                    </>
                  )}
                </div>
              ))}
              {!questionnaireComplete && (
                <p className="apply-hint apply-gate">Please answer all required questions (marked *) to continue.</p>
              )}
              <button type="submit" className="apply-btn" disabled={!questionnaireComplete || busy}>
                {busy ? <Loader2 className="spin" size={18} /> : null} Submit application
              </button>
            </form>
          )}

          {/* STEP — done */}
          {step === 'done' && (
            <div className="apply-step apply-done">
              <img src={successWorker} alt="" role="presentation" className="apply-done-photo" />
              <CheckCircle2 size={40} className="apply-done-icon" />
              <h2>Application received</h2>
              <p>Thank you, {fullName || 'there'}. Our team will review your documents and get in touch by email or phone.</p>
              <Link to="/dashboard" className="apply-btn apply-btn-link">Go to my dashboard</Link>
              <Link to="/" className="apply-link-btn" style={{ marginTop: '0.75rem', display: 'inline-block' }}>Browse more jobs</Link>
            </div>
          )}
          </>
          )}
        </div>
      </main>
      <Footer />

      {splitOpen && (
        <SplitModal
          slots={activeUploadSlots.filter((s) => !String(s.type).startsWith('dt:'))}
          onAssembled={onAssembled}
          onClose={() => setSplitOpen(false)}
        />
      )}
    </>
  );
}
